// src/pages/GroupTicketDesigner.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useGroupTicketDesign from "../hooks/useGroupTicketDesign";
import TicketPreview from "../components/ticketDesign/TicketPreview";
import SectionControls from "../components/ticketDesign/SectionControls";
import ColorInput from "../components/ticketDesign/ColorInput";
import NumberInput from "../components/ticketDesign/NumberInput";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { sampleTicketData } from "../services/ticketDesignApi";
import { saveGroupDesign } from "../services/groupTicketDesignApi";
import { FONT_OPTIONS, WEIGHT_OPTIONS } from "../utils/fonts";
import DesignerUI from "../components/ticketDesign/DesignerUI";
import { useSidebarStore } from "../store/useSidebarStore";
import { useSite } from "../store/useSite";

const PREVIEW_VENUE = "Indira Gandhi National Auditorium";

const getPreviewVenue = (eventData) => {
  const venue = eventData?.venue || eventData?.location || "";
  return String(venue).trim() || PREVIEW_VENUE;
};

const formatPreviewDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatPreviewTime = (value, fallbackDate) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  const parsed = raw.includes("T")
    ? new Date(raw)
    : new Date(`1970-01-01T${raw}`);

  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  if (fallbackDate) {
    const combined = new Date(`${fallbackDate}T${raw}`);
    if (!Number.isNaN(combined.getTime())) {
      return combined.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
  }

  return raw;
};

const formatPreviewDateRange = (startValue, endValue) => {
  const start = startValue ? new Date(startValue) : null;
  const end = endValue ? new Date(endValue) : null;

  if (!start || Number.isNaN(start.getTime())) return "";
  if (!end || Number.isNaN(end.getTime())) return formatPreviewDate(startValue);

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (sameDay) {
    return formatPreviewDate(startValue);
  }

  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameYear) {
    const startText = start.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
    const endText = end.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `${startText} to ${endText}`;
  }

  return `${formatPreviewDate(startValue)} to ${formatPreviewDate(endValue)}`;
};

export default function GroupTicketDesigner() {
  const { id } = useParams();
  const groupId = id;
  const navigate = useNavigate();
  const { setSidebarOpen } = useSidebarStore();
  const { eventData } = useSite();

  const { design, setDesign, replaceDesign, initialDesign, markSavedDesign } = useGroupTicketDesign(groupId);
  const [isSaving, setIsSaving] = useState(false);

  const [data, setData] = useState({
    ...sampleTicketData(),
    ticketName: "Group Ticket", 
    eventName: "My Event",
    ticketId: "TE048703-AA00003"
  });
  const [selectedSection, setSelectedSection] = useState("canvas");
  const [liveTicketId, setLiveTicketId] = useState(data.ticketId);

  useEffect(() => {
    if (eventData) {
      const combinedDateText = formatPreviewDateRange(
        eventData.start_date,
        eventData.end_date || eventData.start_date,
      );
      const startTimeText =
        formatPreviewTime(eventData.start_time, eventData.start_date) ||
        formatPreviewTime(eventData.start_date);
      const previewDateText = startTimeText
        ? `${combinedDateText} | ${startTimeText}`
        : combinedDateText;

      setData((prev) => ({
        ...prev,
        eventName: eventData.event_name || prev.eventName,
        venue: getPreviewVenue(eventData),
        date: previewDateText || prev.date,
        ticketValidDates: combinedDateText || prev.ticketValidDates,
        start_time: startTimeText || prev.start_time,
      }));
    }
  }, [eventData]);

  const [canvasUnit, setCanvasUnit] = useState("px");

 const PX_PER = { px: 1, in: 96, cm: 37.7953, mm: 3.77953 };
  const UNIT_DECIMALS = { px: 0, in: 2, cm: 2, mm: 1 };
  const toUnit = (px) => +(px / PX_PER[canvasUnit]).toFixed(UNIT_DECIMALS[canvasUnit]);
  const toPx  = (val) => Math.round(val * PX_PER[canvasUnit]);

  const CANVAS_PRESETS = [
    { label: "Custom", w: null, h: null },
    { label: "Credit Card (85.6 × 53.9 mm)", w: 323, h: 204 },
    { label: "Business Card (90 × 55 mm)",   w: 340, h: 208 },
    { label: "A6 Ticket (148 × 105 mm)",     w: 559, h: 397 },
    { label: "Event Badge (102 × 76 mm)",    w: 385, h: 287 },
    { label: "Square (100 × 100 mm)",        w: 378, h: 378 },
  ];

  useEffect(() => {
    setData((d) => ({ ...d, ticketId: liveTicketId }));
  }, [liveTicketId]);

  const onDragEnd = (result) => {
    if (!design || !result.destination) return;
    const newOrder = Array.from(design.sectionsOrder);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    setDesign({ sectionsOrder: newOrder });
  };

  const updateCanvas = useCallback((patch) => {
    setDesign((prev) => ({ ...prev, canvas: { ...prev.canvas, ...patch } }));
  }, [setDesign]);

  const updateHeader = useCallback((patch) => {
    setDesign((prev) => ({ ...prev, header: { ...prev.header, ...patch } }));
  }, [setDesign]);

  const toggleSection = (id, enabled) => {
    setDesign((prev) => ({
      ...prev,
      sections: { ...prev.sections, [id]: { ...prev.sections[id], enabled } },
    }));
  };

  const updateSection = useCallback((id, nextOrPatch) => {
    setDesign((prev) => {
      const prevSec = prev.sections[id] || {};
      const next =
        typeof nextOrPatch === "function"
          ? nextOrPatch(prevSec)
          : { ...prevSec, ...nextOrPatch };
      return {
        ...prev,
        sections: { ...prev.sections, [id]: next },
      };
    });
  }, [setDesign]);

  const handleSave = async (options = {}) => {
    const {
      designOverride,
      shouldNavigate = true,
      successMessage = "Group Ticket design saved successfully!",
    } = options;
    const designToSave = designOverride || design;

    if (!groupId || !designToSave) {
      toast.error("Missing groupId or design", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    
    setIsSaving(true);
    try {
      await saveGroupDesign(groupId, designToSave);
      markSavedDesign(designToSave);
      toast.success(successMessage, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      if (shouldNavigate) {
        setTimeout(() => {
          navigate(-1); // Go back to previous page
        }, 1500);
      }
      return true;
    } catch (e) {
      console.error("Error saving group ticket design:", e);
      toast.error(e?.message || "Failed to save group ticket design", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackgroundImageChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image size should be less than 10MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        updateCanvas({ backgroundImage: event.target.result });
      };
      reader.readAsDataURL(file);
    }
  }, [updateCanvas]);

  const handleRemoveBackgroundImage = useCallback(() => {
    updateCanvas({ backgroundImage: "", backgroundImageOpacity: 0.5 });
  }, [updateCanvas]);

  const controlsPanel = useMemo(() => {
    if (!design) return null;

    if (selectedSection === "background") {
      return (
        <div className="controls-group">
          <label className="ctrl">
            <span>Upload Background Image</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleBackgroundImageChange}
              style={{ marginTop: "4px", width: "100%" }}
            />
          </label>
          {design.canvas.backgroundImage ? (
            <>
              <div style={{ marginTop: "12px", marginBottom: "12px" }}>
                <label className="ctrl" style={{ marginBottom: "8px" }}>
                  <span>Preview</span>
                </label>
                <img
                  src={design.canvas.backgroundImage}
                  alt="Background preview"
                  style={{
                    width: "100%",
                    maxHeight: "150px",
                    objectFit: "contain",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                    backgroundColor: "#f5f5f5",
                  }}
                />
              </div>
              <label className="ctrl">
                <span>Opacity (%)</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={Math.round((design.canvas.backgroundImageOpacity ?? 0.5) * 100)}
                  onChange={(e) => {
                    let inputValue = e.target.value;
                    
                    if (inputValue.length > 1 && inputValue.startsWith('0')) {
                      inputValue = inputValue.replace(/^0+/, '') || '0';
                      e.target.value = inputValue;
                    }
                    
                    const numValue = inputValue === "" ? 0 : parseInt(inputValue, 10);
                    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                      const opacity = numValue / 100;
                      updateCanvas({ backgroundImageOpacity: opacity });
                    }
                  }}
                />
              </label>
              <button
                className="btn"
                onClick={handleRemoveBackgroundImage}
                style={{ width: "100%", marginTop: "12px" }}
              >
                Remove Background Image
              </button>
            </>
          ) : (
            <div style={{ marginTop: "12px", padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "14px", border: "1px dashed #d1d5db", borderRadius: "8px", background: "#f9fafb" }}>
              No background image uploaded.
            </div>
          )}
        </div>
      );
    }

    if (selectedSection === "canvas") {
      return (
          <div className="controls-group">
            <ColorInput
              label="Background"
              value={design.canvas.background}
              onChange={(v) => updateCanvas({ background: v })}
            />

            <ColorInput
              label="Border color"
              value={design.canvas.borderColor}
              onChange={(v) => updateCanvas({ borderColor: v })}
            />
            <NumberInput
              label="Border width (px)"
              value={design.canvas.borderWidth}
              min={1}
              max={8}
              onChange={(v) => updateCanvas({ borderWidth: v })}
            />
            <NumberInput
              label="Border radius (px)"
              value={design.canvas.borderRadius}
              min={0}
              max={40}
              onChange={(v) => updateCanvas({ borderRadius: v })}
            />
            <NumberInput
              label="Padding (px)"
              value={design.canvas.padding}
              min={0}
              max={40}
              onChange={(v) => updateCanvas({ padding: v })}
            />

            {/* ── Unit switcher ────────────────────────────── */}
            <label className="ctrl" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
              <span style={{ marginBottom: 4 }}>Dimension Unit</span>
              <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 8, padding: 2 }}>
                {['px','in','cm','mm'].map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setCanvasUnit(u)}
                    style={{
                      padding: '2px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                      background: canvasUnit === u ? '#fff' : 'transparent',
                      color: canvasUnit === u ? '#2563eb' : '#6b7280',
                      boxShadow: canvasUnit === u ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
                      transition: 'all .15s',
                    }}
                  >{u}</button>
                ))}
              </div>
            </label>

            {/* ── Preset sizes ────────────────────────────── */}
            <label className="ctrl">
              <span>Preset Size</span>
              <select
                onChange={(e) => {
                  const p = CANVAS_PRESETS[+e.target.value];
                  if (p.w) updateCanvas({ width: p.w, height: p.h });
                }}
                defaultValue={0}
              >
                {CANVAS_PRESETS.map((p, i) => (
                  <option key={i} value={i}>{p.label}</option>
                ))}
              </select>
            </label>

            {/* ── Width / Height with unit conversion ───────────── */}
            <label className="ctrl">
              <span>Width ({canvasUnit})</span>
              <input
                type="number"
                step={canvasUnit === 'px' ? 1 : canvasUnit === 'mm' ? 0.1 : 0.01}
                min={toUnit(100)}
                max={toUnit(800)}
                value={toUnit(design.canvas.width)}
                onChange={(e) => updateCanvas({ width: toPx(+e.target.value) })}
              />
            </label>
            <label className="ctrl">
              <span>Height ({canvasUnit})</span>
              <input
                type="number"
                step={canvasUnit === 'px' ? 1 : canvasUnit === 'mm' ? 0.1 : 0.01}
                min={toUnit(100)}
                max={toUnit(800)}
                value={toUnit(design.canvas.height)}
                onChange={(e) => updateCanvas({ height: toPx(+e.target.value) })}
              />
            </label>
          </div>
        );
    }

    if (selectedSection === "header") {
      return (
        <div className="controls-group">
          <label className="ctrl row-start">
            <input
              type="checkbox"
              checked={design.header.enabled}
              onChange={(e) => updateHeader({ enabled: e.target.checked })}
            />
            <span>Show header</span>
          </label>
          <ColorInput
            label="Header background"
            value={design.header.bg}
            onChange={(v) => updateHeader({ bg: v })}
          />
          <ColorInput
            label="Header text color"
            value={design.header.color}
            onChange={(v) => updateHeader({ color: v })}
          />
          <NumberInput
            label="Header height"
            value={design.header.height}
            min={36}
            max={120}
            onChange={(v) => updateHeader({ height: v })}
          />
          <label className="ctrl">
            <span>Text</span>
            <input
              className="ctrl-text"
              value={design.header.text}
              onChange={(e) => updateHeader({ text: e.target.value })}
            />
          </label>
          <label className="ctrl">
            <span>Font</span>
            <select
              value={design.header.fontFamily}
              onChange={(e) => updateHeader({ fontFamily: e.target.value })}
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          <label className="ctrl">
            <span>Weight</span>
            <select
              value={design.header.fontWeight}
              onChange={(e) =>
                updateHeader({ fontWeight: Number(e.target.value) })
              }
            >
              {WEIGHT_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </label>
          <NumberInput
            label="Font size"
            value={design.header.fontSize}
            min={10}
            max={28}
            onChange={(v) => updateHeader({ fontSize: v })}
          />
          <label className="ctrl">
            <span>Align</span>
            <select
              value={design.header.align}
              onChange={(e) => updateHeader({ align: e.target.value })}
            >
              <option value="left">left</option>
              <option value="center">center</option>
              <option value="right">right</option>
            </select>
          </label>
        </div>
      );
    }

    const def = design.sections[selectedSection];
    if (!def) return null;

    return (
      <SectionControls
        id={selectedSection}
        def={def}
        onChange={(next) => updateSection(selectedSection, next)}
        hideHeadingControls={true}
      />
    );
  }, [design, selectedSection, canvasUnit, updateCanvas, updateHeader, updateSection, handleBackgroundImageChange, handleRemoveBackgroundImage]);

  if (!design) return null;

  return (
    <>
      <ToastContainer />
      <DesignerUI 
        title="Group Ticket Designer"
        design={design}
        initialDesign={initialDesign}
        setDesign={setDesign}
        replaceDesign={replaceDesign}
        data={data}
        setData={setData}
        isSaving={isSaving}
        onSave={handleSave}
        onBack={() => navigate(-1)}
        hidePairHeading={true}
      />
    </>
  );
}
